/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { TRecordingsStatusDetails } from "@tivocorp/data-domain-lib";

import { RecordingsStatusInteractor } from "../../model/interactor/shared";
import { Log } from "../../model/logging";
import type { UnityRootState } from "../UnityAppStore";
import {
  LEGACY_RECORDING_STATUS,
  recordingScheduled
} from "../utility/recordingEvents";
import { RecordingStatusV2 } from "../../model/types";
import type {
  RecordingsStatusState,
  TRecordingStatusEntry
} from "./recordingsStatus.types";

const TAG: string = "recordingsStatusSlice";

const INITIAL_STATE: RecordingsStatusState = {
  maxAge: 0,
  recordingsStatusMap: {}
};

// ─── Helpers ───

/**
 * Transforms the raw recordings status array into a map keyed by offerId.
 */
function createRecordingsStatusMap(
  recordingStatusList: TRecordingsStatusDetails["recordingStatus"]
): Record<string, TRecordingStatusEntry> {
  const map: Record<string, TRecordingStatusEntry> = {};

  if (recordingStatusList === null || recordingStatusList === undefined)
    return map;

  for (const entry of recordingStatusList) {
    if (entry !== null && entry !== undefined) {
      map[entry.offerId] = {
        contentId: entry.contentId,
        recordingStatus: entry.recordingStatus
      };
    }
  }

  return map;
}

// ─── Async Thunks ───

/**
 * Fetches recording status details via the RecordingsStatusInteractor and
 * transforms the response into a map keyed by offerId.
 */
export const fetchRecordingsStatus = createAsyncThunk<
  {
    maxAge: number;
    recordingsStatusMap: Record<string, TRecordingStatusEntry>;
  },
  void,
  { rejectValue: string }
>(
  "recordingsStatus/fetchRecordingsStatus",
  async (_: void, { rejectWithValue }) => {
    try {
      Log.i(TAG, "fetchRecordingsStatus() - Fetching recording status details");
      const interactor: RecordingsStatusInteractor =
        new RecordingsStatusInteractor();
      const result: TRecordingsStatusDetails =
        await interactor.fetchRecordingStatus({
          bypassNavigationCancellation: true
        });

      const recordingsStatusMap: Record<string, TRecordingStatusEntry> =
        createRecordingsStatusMap(result.recordingStatus);

      Log.d(
        TAG,
        "fetchRecordingsStatus() SUCCESS - maxAge: " +
          result.maxAge +
          ", entries: " +
          Object.keys(recordingsStatusMap).length
      );

      return { maxAge: result.maxAge, recordingsStatusMap };
    } catch (error: unknown) {
      Log.e(TAG, "fetchRecordingsStatus()", error);
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Failed to fetch recordings status");
    }
  }
);

// ─── Slice ───

const recordingsStatusSlice = createSlice({
  name: "recordingsStatus",
  initialState: INITIAL_STATE,
  reducers: {
    resetRecordingsStatus(state: RecordingsStatusState): void {
      state.maxAge = 0;
      state.recordingsStatusMap = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(recordingScheduled, (state, action) => {
        const { offerId, contentId } = action.payload;
        if (offerId !== undefined && contentId !== undefined) {
          let recordingStatus: RecordingStatusV2 =
            RecordingStatusV2.ScheduledAsSingleExplicit;
          if (
            action.payload.recordingStatus ===
              LEGACY_RECORDING_STATUS.IN_PROGRESS ||
            action.payload.recordingStatus ===
              RecordingStatusV2.InProgressRecording
          ) {
            recordingStatus = RecordingStatusV2.InProgressRecording;
          } else if (
            action.payload.recordingStatus ===
              LEGACY_RECORDING_STATUS.RECORDED ||
            action.payload.recordingStatus === RecordingStatusV2.Complete
          ) {
            recordingStatus = RecordingStatusV2.Complete;
          } else if (
            action.payload.recordingStatus ===
            RecordingStatusV2.ScheduledAsOnePass
          ) {
            recordingStatus = RecordingStatusV2.ScheduledAsOnePass;
          }
          state.recordingsStatusMap[offerId] = {
            contentId,
            recordingStatus
          };
        }
      })
      .addCase(fetchRecordingsStatus.fulfilled, (state, action) => {
        state.maxAge = action.payload.maxAge;
        state.recordingsStatusMap = action.payload.recordingsStatusMap;
      })
      .addCase(fetchRecordingsStatus.rejected, (_state, action) => {
        Log.e(
          TAG,
          "fetchRecordingsStatus rejected: " +
            (action.payload !== null && action.payload !== undefined
              ? action.payload
              : "Unknown error")
        );
      });
  }
});

export const { resetRecordingsStatus } = recordingsStatusSlice.actions;

export default recordingsStatusSlice.reducer;

// ─── Selectors ───

export const selectRecordingsStatusMap = (
  state: UnityRootState
): Record<string, TRecordingStatusEntry> =>
  state.recordingsStatus.recordingsStatusMap;

export const selectRecordingsMaxAge = (state: UnityRootState): number =>
  state.recordingsStatus.maxAge;
